/**
 * Helper to safely extract a message from an unknown payload.
 *
 * Checks both 'message' and 'error' keys to handle different backend response formats:
 * - Standard error responses use { message: "..." }
 * - ApiError responses from errorHandler.js use { error: "..." }
 *
 * Priority: 'message' key takes precedence over 'error' key if both exist.
 */
function getMessage(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }

  // Check for 'message' key first (preferred format)
  if ('message' in payload && typeof (payload as { message: unknown }).message === 'string') {
    const msg = (payload as { message: string }).message;
    return msg.length > 0 ? msg : undefined;
  }

  // Fall back to 'error' key (ApiError format from errorHandler.js)
  if ('error' in payload && typeof (payload as { error: unknown }).error === 'string') {
    const msg = (payload as { error: string }).error;
    return msg.length > 0 ? msg : undefined;
  }

  return undefined;
}

/**
 * Base class for API errors to allow for instanceof checks.
 */
export class ApiError extends Error {
  public status: number;
  public payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = this.constructor.name;
    this.status = status;
    this.payload = payload;
  }
}

/**
 * Represents a 401 Unauthorized error.
 */
export class AuthenticationError extends ApiError {
  constructor(payload: unknown) {
    super(getMessage(payload) ?? 'Authentication failed. Please log in again.', 401, payload);
  }
}

/**
 * Represents a session expiration where a token refresh failed.
 */
export class SessionExpiredError extends ApiError {
  constructor() {
    super('Your session has expired. Please log in again.', 401, { message: 'Your session has expired. Please log in again.' });
  }
}

/**
 * Represents a 404 Not Found error.
 */
export class NotFoundError extends ApiError {
  constructor(payload: unknown) {
    super(getMessage(payload) ?? 'The requested resource was not found.', 404, payload);
  }
}

/**
 * Represents a generic 5xx server error.
 */
export class ServerError extends ApiError {
  constructor(payload: unknown) {
    super(getMessage(payload) ?? 'An unexpected server error occurred.', 500, payload);
  }
}

/**
 * Represents a timeout error during bootstrap or other long-running operations.
 * This is a client-side error (no HTTP status), so we use 0 as the status code.
 */
export class BootstrapTimeoutError extends ApiError {
  constructor(timeoutSeconds: number) {
    super(
      `The application failed to load within ${timeoutSeconds} seconds. Please check your network connection and try again.`,
      0,
      { message: `Bootstrap timeout after ${timeoutSeconds} seconds`, timeoutSeconds }
    );
  }
}

/**
 * Represents a transient network or server error that may be retried.
 * Used when all retry attempts have been exhausted.
 */
export class RetryExhaustedError extends ApiError {
  public readonly attempts: number;
  public readonly lastError: Error;

  constructor(attempts: number, lastError: Error) {
    super(
      `Failed to connect after ${attempts} attempts. Please check your network connection and try again.`,
      0,
      { message: `Retry exhausted after ${attempts} attempts`, attempts, lastErrorMessage: lastError.message }
    );
    this.attempts = attempts;
    this.lastError = lastError;
  }
}

// NOTE: CsrfTokenError class removed per ADR-032 (Pure Bearer Token Authentication).
// Bearer tokens provide inherent CSRF protection since they are not automatically
// attached by the browser like cookies.